import * as React from "react"
import { useEffect, useState } from "react"
import {
    Box,
    Chip,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    Link,
    Rating,
    Skeleton,
    Tab,
    Tabs,
    Typography,
} from "@mui/material"
import { FlagsIconSeeker, IconSeeker, ImageSeeker } from "@ui-seeker/core"

import { useScrapingDataReviewDetails } from "../@hooks"
import { detailsRow, detailsTab, imageViewer, showDetails, sitesOptions } from "../@signals"

const CURRENCY_SYMBOLS = {
    USD: '$', EUR: '€', GBP: '£', MXN: '$', BRL: 'R$', ARS: '$', CLP: '$',
    COP: '$', PEN: 'S/', CAD: 'C$', CHF: 'CHF', SEK: 'kr', PLN: 'zł',
}


function formatPrice(value, currency) {
    if (value === null || value === undefined) return null
    const symbol = CURRENCY_SYMBOLS[currency] || currency || ''
    // A space: '$19499900.00' ran together and was unreadable.
    return symbol ? `${symbol} ${Number(value).toFixed(2)}` : Number(value).toFixed(2)
}


/**
 * Open the viewer on a whole gallery, positioned on the one that was clicked.
 *
 * The point of passing the list rather than the single URL: a product with six
 * photos is one thing you page through, and closing the viewer to click the
 * next thumbnail is exactly the friction this replaces.
 */
function openGallery(urls, index, title, kind = 'image') {
    if (!urls?.length) return
    imageViewer.value = { urls, index, title: title || 'Image', kind }
}


function EmptyNote({ children }) {
    return <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
        {children}
    </Typography>
}


/** `Label: value`, the drawer's unit, same shape the table cells use. */
/**
 * Every seller that had this product on the day it was captured.
 *
 * The table can only show one of them — DIPI_LAST_ITEM_RAW keeps the last
 * capture written, which on a marketplace is whichever seller was scraped last
 * — so this is where the rest of the page lives: who held the buybox, at what
 * total, and how far the row on screen was from it.
 *
 * Sorted buybox first, then by total price. Fetched with the rest of the
 * drawer, in the same request, on open: a primary-key lookup, ~16 ms.
 */
function OffersPanel({ offers, sellerName, sellerCode, captureDate }) {
    if (!offers.length) {
        return (
            <Typography variant="body2" sx={{ color: '#9ca3af', py: 3, textAlign: 'center' }}>
                No marketplace offers captured for this product that day.
            </Typography>
        )
    }

    const shown = (sellerCode || sellerName || '').trim().toLowerCase()

    return (
        <Box>
            <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1 }}>
                {offers.length} {offers.length === 1 ? 'seller' : 'sellers'} on {captureDate || 'the capture date'} · total = actual + shipping
            </Typography>

            {offers.map((offer) => {
                const identity = (sellerCode ? offer.sellerCode : offer.sellerName) || offer.seller
                const isShown = shown && (identity || '').trim().toLowerCase() === shown
                // Amazon writes a merchant id, Otto writes the shop name in the
                // same field. Showing the code under the name only when they
                // differ keeps the second line from repeating the first.
                const showCode = offer.sellerCode && offer.sellerCode !== offer.sellerName
                return (
                    <Box
                        key={`${offer.seller}-${offer.totalPrice}`}
                        sx={{
                            border: '1px solid',
                            borderColor: offer.buybox ? '#bbf7d0' : '#e5e7eb',
                            backgroundColor: offer.buybox ? '#f0fdf4' : '#fff',
                            borderRadius: 1, p: 1, mb: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {offer.sellerName || offer.seller || '(no seller)'}
                                </Typography>
                                {showCode && (
                                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                                        {offer.sellerCode}
                                    </Typography>
                                )}
                            </Box>
                            {Boolean(offer.buybox) && (
                                <Chip size="small" label="Buybox" sx={{
                                    height: 17, fontSize: 9, fontWeight: 700,
                                    color: '#15803d', backgroundColor: '#dcfce7',
                                }} />
                            )}
                            {/* Which of them the table was showing — the whole
                                reason this panel exists. */}
                            {isShown && (
                                <Chip size="small" label="On the table" sx={{
                                    height: 17, fontSize: 9, fontWeight: 700,
                                    color: '#b45309', backgroundColor: '#fef3c7',
                                }} />
                            )}
                            <Chip size="small" label={offer.externalSeller ? 'Third party' : 'Retailer'} sx={{
                                height: 17, fontSize: 9,
                                color: '#4b5563', backgroundColor: '#f3f4f6',
                            }} />
                        </Box>

                        {/* Total on the right, on its own: it is what these
                            offers are ranked by, and the one number to compare
                            across sellers. The rest reads left to right in the
                            order it is built — actual, list, shipping. */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            {/* Three fixed rows rather than one that wraps: the
                                two prices, then shipping, then availability.
                                Letting them flow put the same field at the end
                                of a line on one card and on its own line on the
                                next, so no two cards read alike. */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Field label="Actual:" value={formatPrice(offer.actualPrice, offer.currency)} />
                                    <Field label="List:" value={formatPrice(offer.listPrice, offer.currency)} />
                                </Box>
                                <Field
                                    label="Shipping:"
                                    value={offer.freeShipping ? 'Free' : formatPrice(offer.shippingPrice, offer.currency)}
                                />
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Field label="Stock:" value={offer.stock ? 'In stock' : 'Out of stock'} />
                                    {offer.deliveryDays != null && (
                                        <Field label="Delivery:" value={`${offer.deliveryDays} d`} />
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, display: 'block' }}>
                                    Total
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    {formatPrice(offer.totalPrice, offer.currency) || '—'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )
            })}
        </Box>
    )
}


function Field({ label, value, fallback = '—' }) {
    return (
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {value === null || value === undefined || value === '' ? fallback : value}
            </Typography>
        </Box>
    )
}


/**
 * A gallery, with the two empty states kept apart.
 *
 * "Nothing scraped" is normal — most customers have no product-page media at
 * all. "The row said there was media and none came back" is a defect, and
 * collapsing both into one dash would hide it.
 */
function Gallery({ title, urls, expected, productTitle, kind = 'image' }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                    {title}
                </Typography>
                {urls?.length > 0 && (
                    <Chip size="small" label={urls.length} sx={{ height: 16, fontSize: 10 }} />
                )}
            </Box>
            {urls?.length
                ? <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {urls.map((url, i) => (
                        // A video URL in an <img> renders as a broken tile. It
                        // gets a play affordance instead, and the viewer decides
                        // whether the browser can actually play it.
                        kind === 'video'
                        ? <Box
                            key={`${url}-${i}`}
                            title={url}
                            onClick={() => openGallery(urls, i, `${productTitle || title} — ${title}`, 'video')}
                            sx={{
                                width: 56, height: 56, borderRadius: 1, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: '#111827',
                                '&:hover': { backgroundColor: '#1f2937' },
                            }}
                        >
                            <IconSeeker icon="play" sx={{ fontSize: 16, color: '#fff' }} />
                        </Box>
                        : <ImageSeeker
                            key={`${url}-${i}`}
                            src={url}
                            title="Click to enlarge"
                            onClick={() => openGallery(urls, i, `${productTitle || title} — ${title}`)}
                            sx={{
                                width: 56, height: 56, objectFit: 'contain',
                                border: '1px solid #e5e7eb', borderRadius: 1,
                                cursor: 'zoom-in',
                            }}
                        />
                    ))}
                </Box>
                : <EmptyNote>
                    {expected
                        ? 'The row reports media but none came back — worth a look.'
                        : 'Not scraped.'}
                </EmptyNote>}
        </Box>
    )
}


function LongText({ title, value, flagged }) {
    const [open, setOpen] = useState(false)
    if (!value) {
        return (
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, display: 'block' }}>
                    {title}
                </Typography>
                <EmptyNote>
                    {flagged
                        ? 'The row says this is filled but it came back empty.'
                        : 'Not scraped for this product.'}
                </EmptyNote>
            </Box>
        )
    }
    return (
        <Box sx={{ mb: 1.5 }}>
            <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => setOpen(!open)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12 }}
            >
                <IconSeeker icon={open ? 'chevron-down' : 'chevron-right'} sx={{ fontSize: 11 }} />
                {open ? `Hide ${title.toLowerCase()}` : `Show ${title.toLowerCase()}…`}
            </Link>
            <Collapse in={open}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.75 }}>
                    {value}
                </Typography>
            </Collapse>
        </Box>
    )
}


/**
 * The commercial flags, as one scannable row — the same set the DIPI app shows.
 *
 * Only these five. The content ones that used to be here (description, rich
 * content, images, videos, eyecatchs, EAN) are already answered elsewhere in
 * this drawer: the Description tab renders them, and the table's own columns
 * carry their coverage. Eight icons where five say something was noise.
 *
 * ⚠️ Promo code is missing from the DIPI set on purpose: `DIPI_LAST_ITEM_RAW`
 * has no such column. Inventing one from `loyalty_points` or `member_price`
 * would be a different field wearing its name.
 */
function FlagStrip({ row }) {
    const flags = [
        { icon: 'box-open', label: 'In stock', on: row?.stockOnline === 1 },
        { icon: 'store', label: 'External seller', on: !!row?.externalSeller },
        { icon: 'shop', label: 'Click & collect', on: row?.clickAndCollect === 1 },
        { icon: 'truck', label: 'Free shipping', on: !!row?.freeShipping },
        { icon: 'gift', label: 'Gifts', on: !!row?.gifts },
    ]
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {flags.map(flag => (
                <Box
                    key={flag.label}
                    title={`${flag.label}: ${flag.on ? 'yes' : 'no'}`}
                    sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 30, height: 30, borderRadius: 1,
                        backgroundColor: flag.on ? '#f0fdf4' : '#fef2f2',
                    }}
                >
                    <IconSeeker
                        icon={flag.icon}
                        sx={{ fontSize: 14, color: flag.on ? '#15803d' : '#b91c1c' }}
                    />
                </Box>
            ))}
        </Box>
    )
}


/**
 * The drawer for one row: the product, then its text, media and ids.
 *
 * Fetched on open rather than shipped with the table — the text and media URLs
 * were ~66 % of the payload when the DIPI app sent them inline. The table's
 * `has*` and `num*` fields let the empty states render before it resolves.
 */
export default function ScrapingDataReviewDetailsDrawer() {
    const [tab, setTab] = useState(0)
    const { isLoading, details, row } = useScrapingDataReviewDetails()
    const offers = details?.offers || []

    // The sellers chip in the table opens this drawer straight on Offers.
    useEffect(() => {
        if (detailsRow.value) setTab(detailsTab.value)
    }, [detailsRow.value])

    const handleClose = () => { detailsRow.value = null; detailsTab.value = 0; setTab(0) }
    // From the row's own site, looked up in the catalogue — not from the
    // filter: the table can now hold several sites at once, so the filter no
    // longer says which one this product came from.
    const site = (sitesOptions.value || []).find(option => option.value === row?.siteID)
    const price = formatPrice(row?.actualPrice, row?.currency)

    return (
        <Drawer anchor="right" open={showDetails.value} onClose={handleClose}>
            <Box sx={{ width: 520, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#6b7280' }}>
                        Product details
                    </Typography>
                    <IconButton size="small" onClick={handleClose}>
                        <IconSeeker icon="xmark" sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>

                {/* Identity first, the way the row reads: brand, where, when. */}
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                        {row?.brandRaw || <Typography component="span" variant="h6" sx={{ color: '#b91c1c' }}>
                            (no brand)
                        </Typography>}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                        {site?.countryCode && <FlagsIconSeeker countryName={site.countryCode} />}
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {site?.label || `Site ${row?.siteID}`}
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                        {row?.date || '—'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    {row?.thumbnail
                        ? <ImageSeeker
                            src={row.thumbnail}
                            title="Click to enlarge"
                            onClick={() => openGallery([row.thumbnail], 0, row.productTitle || row.productID)}
                            sx={{
                                maxWidth: 240, maxHeight: 240, objectFit: 'contain',
                                cursor: 'zoom-in',
                            }}
                        />
                        : <Box sx={{
                            width: 240, height: 160, borderRadius: 1,
                            border: '1px dashed #e5e7eb', display: 'flex',
                            flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                        }}>
                            <IconSeeker icon="image-slash" sx={{ fontSize: 24, color: '#d1d5db' }} />
                            <EmptyNote>No thumbnail scraped</EmptyNote>
                        </Box>}
                </Box>

                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row?.productTitle || <EmptyNote>No title scraped</EmptyNote>}
                    {row?.productUrl && (
                        <Link href={row.productUrl} target="_blank" rel="noreferrer" sx={{ ml: 0.75 }}>
                            <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 12 }} />
                        </Link>
                    )}
                </Typography>
                <Field label="EAN:" value={row?.ean} />

                <Typography variant="h6" sx={{ textAlign: 'right', mt: 1 }}>
                    {price || <Typography component="span" variant="h6" sx={{ color: '#b91c1c' }}>
                        no price
                    </Typography>}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Tabs value={tab} onChange={(_e, value) => setTab(value)} sx={{ mb: 2 }} variant="fullWidth">
                    <Tab label="Features" sx={{ minHeight: 40 }} />
                    <Tab label="Description" sx={{ minHeight: 40 }} />
                    <Tab label="Internal Info" sx={{ minHeight: 40 }} />
                    <Tab
                        label={offers.length > 1 ? `Offers (${offers.length})` : 'Offers'}
                        sx={{ minHeight: 40 }}
                    />
                </Tabs>

                {isLoading && <Skeleton variant="rectangular" height={280} />}

                {!isLoading && details && tab === 0 && (
                    <Box>
                        <FlagStrip row={row} />
                        <Field label="Variant raw:" value={row?.variantRaw} />
                        <Field label="Reference:" value={row?.reference} />
                        <Field label="Seller:" value={row?.sellerName} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                                Rating:
                            </Typography>
                            {row?.rating
                                ? <>
                                    <Rating value={Number(row.rating)} precision={0.1} size="small" readOnly />
                                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                        ({Number(row.rating).toFixed(1)}
                                        {row.numOpinions ? ` · ${row.numOpinions} reviews` : ''})
                                    </Typography>
                                </>
                                : <EmptyNote>Not scraped.</EmptyNote>}
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Eyecatchs
                            </Typography>
                            {details.eyecatchs?.length
                                ? <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {details.eyecatchs.map((label, i) => (
                                        <Chip key={`${label}-${i}`} size="small" label={label} />
                                    ))}
                                </Box>
                                : <EmptyNote>None.</EmptyNote>}
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                            <LongText title="Last review" value={details.lastReview} />
                            <Field label="Review title:" value={details.lastReviewTitle} />
                        </Box>
                    </Box>
                )}

                {!isLoading && details && tab === 1 && (
                    <Box>
                        <Gallery title="Images" urls={details.images} expected={row?.numImages > 0} productTitle={row?.productTitle} />
                        <Gallery title="Videos" urls={details.videos} expected={row?.numVideos > 0} productTitle={row?.productTitle} kind="video" />
                        <Gallery title="Rich content images" urls={details.richContentImages} expected={row?.numRichContentImages > 0} productTitle={row?.productTitle} />
                        <Gallery title="Rich content videos" urls={details.richContentVideos} expected={row?.numRichContentVideos > 0} productTitle={row?.productTitle} kind="video" />
                        <Divider sx={{ my: 1.5 }} />
                        <LongText title="Description" value={details.description} flagged={row?.hasDescription} />
                        <LongText title="Rich content text" value={details.richContentText} flagged={row?.hasRichContentText} />
                    </Box>
                )}

                {!isLoading && details && tab === 2 && (
                    <Box>
                        <Field label="Item ID:" value={row?.itemID} />
                        <Field label="Product ID:" value={row?.productID} />
                        <Field label="Search ID:" value={row?.searchID} />
                        <Field label="Task ID:" value={row?.taskID} />
                        <Field label="Site ID:" value={row?.siteID} />
                        <Field label="Captured at:" value={row?.timestamp} />
                        <Field label="Rating scores:" value={details.ratingScores} />
                        <Field label="Legacy:" value={row?.legacy} />
                    </Box>
                )}

                {!isLoading && details && tab === 3 && (
                    <OffersPanel
                        offers={offers}
                        sellerName={row?.sellerName}
                        sellerCode={row?.sellerCode}
                        captureDate={details.captureDate}
                    />
                )}
            </Box>
        </Drawer>
    )
}
